import React from "react";
import { useTranslation } from "react-i18next";

import { PaneElement } from "@dashboard/shared/components/SidePanel/shared/PaneElement";
import { featureOn, LimitFeatureTile } from "@dashboard/shared/limits/FeatureTile";
import { FeatureTilesGrid } from "@dashboard/shared/limits/FeatureTilesGrid";
import { getFeature } from "@dashboard/shared/limits/utils";
import { SectionHeader } from "@dashboard/shared/styles";

type Props = {
    features: API.Feature[];
    editable?: boolean;
    onLimitClick?: (limitType: string, currentLimit: number) => void;
    variant?: "section" | "pane";
};

export const SubscriptionLimits = (props: Props) => {
    const { t } = useTranslation(["pages", "common"]);
    const { features, editable = false, onLimitClick, variant = "section" } = props;

    const deviceManagement = getFeature("DeviceManagement", features);

    const header = variant === "section" ? t("common:subscription.limits") : t("common:subscription.limits");

    const Grid = (
        <FeatureTilesGrid>
            <LimitFeatureTile
                limitType="BuildLimit"
                title={t("common:limits.BuildLimit")}
                features={features}
                editable={editable}
                onClick={onLimitClick}
            />

            {deviceManagement?.used === featureOn ? (
                <React.Fragment>
                    <LimitFeatureTile
                        limitType="CommandLimit"
                        title={t("common:limits.CommandLimit")}
                        features={features}
                        editable={false}
                        onClick={onLimitClick}
                    />
                    <LimitFeatureTile
                        limitType="DeviceStateHistoryLimit"
                        title={t("common:limits.DeviceStateHistoryLimit")}
                        features={features}
                        editable={false}
                        onClick={onLimitClick}
                    />
                </React.Fragment>
            ) : null}
            <LimitFeatureTile
                limitType="FDroidRepoLimit"
                title={t("common:limits.FDroidRepoLimit")}
                features={features}
                editable={editable}
                onClick={onLimitClick}
            />
            <LimitFeatureTile
                limitType="FileCountLimit"
                title={t("common:limits.FileCountLimit")}
                features={features}
                editable={editable}
                onClick={onLimitClick}
            />
            <LimitFeatureTile
                title={t("common:limits.FileSizeLimit")}
                limitType="FileSizeLimit"
                features={features}
                editable={editable}
                onClick={onLimitClick}
            />
            <LimitFeatureTile
                limitType="GroupLimit"
                title={t("common:limits.GroupLimit")}
                features={features}
                editable={false}
                onClick={onLimitClick}
            />
            <LimitFeatureTile
                limitType="GroupNestingLimit"
                title={t("common:limits.GroupNestingLimit")}
                features={features}
                editable={false}
                onClick={onLimitClick}
            />
            <LimitFeatureTile
                limitType="LicenseLimit"
                title={t("common:limits.LicenseLimit")}
                features={features}
                editable={editable}
                onClick={onLimitClick}
            />
            <LimitFeatureTile
                limitType="PolicyLimit"
                title={t("common:limits.PolicyLimit")}
                features={features}
                editable={false}
                onClick={onLimitClick}
            />
            <LimitFeatureTile
                limitType="ProductLimit"
                title={t("common:limits.ProductLimit")}
                features={features}
                editable={editable}
                onClick={onLimitClick}
            />
            <LimitFeatureTile
                limitType="ProvisioningLimit"
                title={t("common:limits.ProvisioningLimit")}
                features={features}
                editable={editable}
                onClick={onLimitClick}
            />
            <LimitFeatureTile
                limitType="StreamSessionLimit"
                title={t("common:limits.StreamSessionLimit")}
                features={features}
                editable={false}
                onClick={onLimitClick}
            />
            <LimitFeatureTile
                limitType="UserLimit"
                title={t("common:limits.UserLimit")}
                features={features}
                editable={editable}
                onClick={onLimitClick}
            />
        </FeatureTilesGrid>
    );

    if (variant === "pane") {
        return (
            <>
                <PaneElement.Header>{header}</PaneElement.Header>
                {Grid}
            </>
        );
    }

    return (
        <>
            <SectionHeader>{header}</SectionHeader>
            {Grid}
        </>
    );
};
